import React from 'react';
import { Form, Input } from 'antd';
import { capitalize } from 'lodash';

import '../generic/Table/TableV3.css';
import TextArea from 'antd/es/input/TextArea';

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'Number' | 'Text' | 'Money' | 'Percentage' | 'TextArea';
  record: any;
  children: React.ReactNode;
  required: boolean;
  defaultValue?: any;
}

export const EditableCell: React.FC<EditableCellProps> = React.memo(
  ({
    editing,
    dataIndex,
    record,
    children,
    inputType,
    required,
    defaultValue,
    ...restProps
  }) => {
    let inputNode;

    switch (capitalize(inputType)) {
      case 'Money':
        inputNode = <Input defaultValue={defaultValue} />;
        break;
      case 'Percentage':
        inputNode = (
          <Input
            suffix="%"
            className="text-align-right"
            defaultValue={defaultValue}
            size="middle"
          />
        );
        break;
      case 'Textarea':
        inputNode = <TextArea defaultValue={defaultValue} />;
        break;
      default:
        inputNode = <Input defaultValue={defaultValue} />;
        break;
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0, padding: 0 }}
            rules={[{ required }]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  },
);

export const EditableCellMitigation = React.memo(
  ({
    title,
    editable,
    dataIndex,
    colIndex,
    record,
    propertyToMap,
    editing,
    onlyPercentage,
    children,
    ...restProps
  }: any) => {
    if (onlyPercentage) {
      return (
        <td {...restProps}>
          {editing && dataIndex === propertyToMap ? (
            <Form.Item style={{ margin: 0 }}>
              {record[propertyToMap]?.map((loc: any, index: number) => {
                if (colIndex !== index) return null;
                return (
                  <Form.Item
                    key={colIndex}
                    name={`percentage_${colIndex}`}
                    style={{ margin: 0 }}
                    rules={[
                      {
                        validator(_, value) {
                          if (value !== undefined && value >= 0) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error('Cannot be a negative number!'),
                          );
                        },
                      },
                    ]}
                    getValueProps={(inputValue) =>
                      inputValue === 0
                        ? { value: undefined }
                        : { value: inputValue }
                    }
                  >
                    <Input
                      suffix="%"
                      style={{
                        width: '100%',
                        marginBottom: 0,
                      }}
                      className={`text-align-right`}
                      placeholder="0"
                    />
                  </Form.Item>
                );
              })}
            </Form.Item>
          ) : (
            children
          )}
        </td>
      );
    }

    return (
      <td {...restProps}>
        {(() => {
          if (!editing) {
            return children;
          }

          if (dataIndex === propertyToMap) {
            return (
              <Form.Item style={{ margin: 0 }}>
                {record[propertyToMap].map((_: any, index: number) => {
                  if (colIndex !== index) return null;
                  return (
                    <div key={colIndex} className="flex flex-col gap-2">
                      <Form.Item
                        name={`percentage_${colIndex}`}
                        style={{ margin: 0 }}
                        getValueProps={(inputValue) =>
                          inputValue === 0
                            ? { value: undefined }
                            : { value: inputValue }
                        }
                      >
                        <Input
                          suffix="%"
                          style={{
                            width: '100%',
                            marginBottom: 0,
                          }}
                          className={`text-align-right`}
                          placeholder="0"
                        />
                      </Form.Item>
                      <Form.Item
                        name={`delay_${colIndex}`}
                        style={{ margin: 0 }}
                        getValueProps={(inputValue) =>
                          inputValue === 0
                            ? { value: undefined }
                            : { value: inputValue }
                        }
                      >
                        <Input
                          suffix="months"
                          style={{
                            width: '100%',
                            marginBottom: 0,
                          }}
                          className={`text-align-right`}
                          placeholder="0"
                        />
                      </Form.Item>
                    </div>
                  );
                })}
              </Form.Item>
            );
          }

          return (
            <Form.Item
              name={dataIndex}
              style={{ margin: 0 }}
              rules={[{ required: true, message: `Please Input ${title}!` }]}
            >
              <Input />
            </Form.Item>
          );
        })()}
      </td>
    );
  },
);
